import { Link, Navigate, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  LogOut,
  type LucideIcon,
  Menu,
  Search,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logoUrl from './assets/loot-studios-logo.png';
import symbolUrl from './assets/loot-studios-simbolo.png';
import { PERMISSIONS } from '@loot/shared';
import type { Me } from './api.ts';
import { can, logout, useMe } from './auth.tsx';
import { Button, ErrorBox, Loading } from './components/ui.tsx';
import { MasterBadge } from './components/MasterBadge.tsx';
import { setFocusEntity, useFocusEntity } from './focus.ts';
import { LANGUAGES } from './i18n/index.ts';
import { ReportButton } from './feedback.tsx';
import { NotificationBell } from './notifications.tsx';
import {
  activeItemTo,
  activeSectionId,
  breadcrumbs,
  itemsOf,
  locate,
  menuTarget,
  type NavArea,
  type NavGroup,
  type NavItem,
  screensOf,
  sectionPath,
  sectionsOf,
  visibleAreas,
} from './navigation.ts';
import { personInitials, personShortName } from './person-name.ts';

const COLLAPSED_KEY = 'loot.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // navegador sem armazenamento: só não lembra a escolha
  }
}

export function LanguageSwitch() {
  const { i18n, t } = useTranslation();
  return (
    <label className="flex items-center gap-2 text-xs text-muted">
      <span className="sr-only">{t('common.language')}</span>
      <select
        id="language"
        value={i18n.language}
        onChange={(e) => void i18n.changeLanguage(e.target.value)}
        className="loot-select rounded-md border border-control-border bg-sheet py-1.5 pl-2.5 text-xs text-ink"
      >
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {l.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Recuo de cada nível da lateral: tela ou módulo, tela dentro do módulo. */
const DEPTH = ['px-2.5', 'pr-2.5 pl-8'] as const;

/** Um item da lateral: uma tela ou, no menu enxuto, uma seção. */
function MenuLink({
  to,
  label,
  icon: Icon,
  active,
  depth = 0,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  depth?: 0 | 1;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      activeOptions={{ exact: true }}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : undefined}
      className={`flex min-h-9 items-center gap-2.5 rounded-md py-1.5 text-sm ${DEPTH[depth]} ${
        active ? 'bg-brand-soft font-medium text-brand-text' : 'text-ink hover:bg-paper'
      }`}
    >
      <Icon
        size={depth === 0 ? 18 : 16}
        strokeWidth={1.75}
        aria-hidden
        className={`shrink-0 ${active ? 'text-brand-text' : 'text-muted'}`}
      />
      {label}
    </Link>
  );
}

/** Botão que abre e fecha um módulo do departamento aberto. */
function Disclosure({
  label,
  open,
  current,
  controls,
  onToggle,
}: {
  label: string;
  open: boolean;
  /** A tela atual está aqui dentro: o nome fica destacado mesmo fechado. */
  current: boolean;
  controls: string;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronDown : ChevronRight;
  const tone = current ? 'font-medium text-brand-text' : open ? 'font-medium text-ink' : 'text-ink';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className={`flex min-h-9 w-full items-center gap-2.5 rounded-md py-1.5 text-left text-sm hover:bg-paper ${DEPTH[0]} ${tone}`}
    >
      <span className="flex-1">{label}</span>
      <Chevron size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
    </button>
  );
}

/**
 * As telas de um departamento aberto. Menu enxuto (Dados Mestres): a visão geral e uma entrada por
 * seção. Nos outros: as telas soltas e um módulo (grupo com título) de cada vez; departamento com um
 * grupo só mostra as telas direto.
 */
function AreaTree({
  area,
  idPrefix,
  openGroup,
  onToggleGroup,
  onNavigate,
}: {
  area: NavArea;
  idPrefix: string;
  openGroup: string | undefined;
  onToggleGroup: (key: string) => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Só um item marcado: o de endereço mais longo (ver activeItemTo).
  const activeTo = activeItemTo([area], pathname);
  const link = (item: NavItem, depth: 0 | 1) => (
    <MenuLink
      key={item.to}
      to={item.to}
      label={t(item.key)}
      icon={item.icon}
      active={item.to === activeTo}
      depth={depth}
      onNavigate={onNavigate}
    />
  );
  if (area.sections) {
    const activeSection = activeSectionId(area, pathname);
    return (
      <>
        {area.groups
          .filter((g) => !g.section)
          .flatMap((g) => g.items)
          .map((item) => link(item, 0))}
        {sectionsOf(area).map((section) => (
          <MenuLink
            key={section.id}
            to={sectionPath(area, section)}
            label={t(section.key)}
            icon={section.icon}
            active={section.id === activeSection}
            onNavigate={onNavigate}
          />
        ))}
      </>
    );
  }
  if (area.groups.length === 1) return <>{area.groups[0]!.items.map((i) => link(i, 0))}</>;
  const currentGroup = locate([area], pathname).group;
  const module = (group: NavGroup, gi: number) => {
    const id = `${idPrefix}-${area.id}-group-${gi}`;
    const open = openGroup === group.key;
    return (
      <div key={group.key} className="flex flex-col gap-0.5">
        <Disclosure
          label={t(group.key!)}
          open={open}
          current={group === currentGroup && activeTo !== undefined}
          controls={id}
          onToggle={() => onToggleGroup(group.key!)}
        />
        {open && (
          <div id={id} className="flex flex-col gap-0.5">
            {group.items.map((i) => link(i, 1))}
          </div>
        )}
      </div>
    );
  };
  return (
    <>
      {area.groups.map((group, gi) =>
        group.key ? module(group, gi) : group.items.map((i) => link(i, 0)),
      )}
    </>
  );
}

/**
 * Lista da lateral única (ADR 0071): Início, os departamentos e, depois da divisória, Ajuda e Dados
 * Mestres. Entrar num departamento troca a lista pelas telas dele, com o botão de voltar: assim as
 * telas nunca ficam abaixo do fim do menu. Abre sozinha no departamento e no módulo da tela atual.
 */
function SidebarNav({
  areas,
  idPrefix,
  initialArea,
  onNavigate,
}: {
  areas: NavArea[];
  idPrefix: string;
  /** Departamento escolhido na faixa recolhida: abre no lugar do da tela atual. */
  initialArea?: string;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const target = menuTarget(areas, pathname);
  const [openArea, setOpenArea] = useState(initialArea ?? target.areaId);
  const [openGroup, setOpenGroup] = useState(
    initialArea && initialArea !== target.areaId ? undefined : target.groupKey,
  );
  // Mudou de tela: abre o departamento e o módulo dela (ajuste durante a renderização, sem efeito).
  const [syncedPath, setSyncedPath] = useState(pathname);
  if (syncedPath !== pathname) {
    setSyncedPath(pathname);
    setOpenArea(target.areaId);
    setOpenGroup(target.groupKey);
  }
  const currentArea = locate(areas, pathname).area;
  const homeActive = activeItemTo(areas, pathname);
  const opened = areas.find((a) => a.id === openArea && screensOf(a).length > 0);
  const openedId = opened?.id;
  // A tela atual fica à vista na lateral (link direto para uma tela do fim da lista, por exemplo).
  const navRef = useRef<HTMLElement>(null);
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [pathname]);
  // Trocar de lista: volta ao topo, e o foco do teclado não se perde com o botão que sumiu.
  const focusAfter = useRef<string | undefined>(undefined);
  useEffect(() => {
    const nav = navRef.current;
    nav?.parentElement?.scrollTo?.({ top: 0 });
    nav?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ block: 'nearest' });
    if (focusAfter.current) nav?.querySelector<HTMLElement>(focusAfter.current)?.focus();
    focusAfter.current = undefined;
  }, [openedId]);

  const enter = (area: NavArea) => {
    focusAfter.current = '[data-nav-back]';
    setOpenArea(area.id);
    setOpenGroup(area.id === target.areaId ? target.groupKey : undefined);
  };
  const leave = (area: NavArea) => {
    focusAfter.current = `[data-nav-area="${area.id}"]`;
    setOpenArea(undefined);
    setOpenGroup(undefined);
  };

  if (opened) {
    const Icon = opened.icon;
    return (
      <nav ref={navRef} aria-label={t('nav.label')} className="flex flex-col gap-0.5">
        <button
          type="button"
          data-nav-back
          onClick={() => leave(opened)}
          className="flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-muted hover:bg-paper hover:text-ink"
        >
          <ArrowLeft size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
          {t('nav.allAreas')}
        </button>
        <div className="mt-1 mb-1 flex items-start gap-2.5 border-b border-rule px-2.5 pb-2 text-sm font-semibold text-ink">
          <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-brand-text" />
          {t(opened.key)}
        </div>
        <AreaTree
          area={opened}
          idPrefix={idPrefix}
          openGroup={openGroup}
          onToggleGroup={(key) => setOpenGroup((g) => (g === key ? undefined : key))}
          onNavigate={onNavigate}
        />
      </nav>
    );
  }

  const entry = (area: NavArea) => {
    const Icon = area.icon;
    const current = area.id === currentArea?.id;
    if (screensOf(area).length === 0) {
      // Só a página inicial, sem nada para abrir: em preparação ou o Dashboard executivo.
      const home = itemsOf(area)[0]!;
      const active = homeActive === home.to;
      return (
        <Link
          key={area.id}
          to={home.to}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={`flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm ${
            active ? 'bg-brand-soft font-medium text-brand-text' : 'text-ink hover:bg-paper'
          }`}
        >
          <Icon
            size={18}
            strokeWidth={1.75}
            aria-hidden
            className={`shrink-0 ${active ? 'text-brand-text' : 'text-muted'}`}
          />
          <span className="flex-1">{t(area.key)}</span>
        </Link>
      );
    }
    return (
      <button
        key={area.id}
        type="button"
        data-nav-area={area.id}
        onClick={() => enter(area)}
        className={`flex min-h-9 w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-paper ${
          current ? 'font-medium text-brand-text' : 'text-ink'
        }`}
      >
        <Icon
          size={18}
          strokeWidth={1.75}
          aria-hidden
          className={`shrink-0 ${current ? 'text-brand-text' : 'text-muted'}`}
        />
        <span className="flex-1">{t(area.key)}</span>
        <ChevronRight size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
      </button>
    );
  };

  const home = areas.find((a) => a.id === 'home');
  const departments = areas.filter((a) => a.id !== 'home' && !a.bottom);
  const bottom = areas.filter((a) => a.bottom);
  return (
    <nav ref={navRef} aria-label={t('nav.label')} className="flex flex-col gap-0.5">
      {home &&
        itemsOf(home).map((item) => (
          <MenuLink
            key={item.to}
            to={item.to}
            label={t(item.key)}
            icon={item.icon}
            active={item.to === homeActive}
            onNavigate={onNavigate}
          />
        ))}
      <div className="mt-4 mb-1 px-2.5 text-xs font-semibold text-muted">{t('nav.areas')}</div>
      {departments.map(entry)}
      {bottom.length > 0 && <div className="my-2 border-t border-rule" />}
      {bottom.map(entry)}
    </nav>
  );
}

/** Rodapé da lateral: uma linha com a pessoa; conta, idioma e Sair abrem por cima da lista. */
function UserMenu({ me, onNavigate }: { me: Me; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const { displayName, email } = me.user;
  const panelId = 'user-menu-panel';
  return (
    <div ref={box} className="relative min-w-0 flex-1">
      {open && (
        <div
          id={panelId}
          className="absolute bottom-full left-0 z-30 mb-2 flex w-60 flex-col gap-3 rounded-lg border border-rule bg-sheet p-3 text-xs text-muted shadow-lg"
        >
          <div className="flex flex-col gap-0.5">
            {/* O nome abre Meus acessos (o que a pessoa pode fazer e onde). */}
            <Link
              to="/my-access"
              onClick={onNavigate}
              title={t('nav.myAccess')}
              className="self-start text-sm font-medium text-ink hover:underline"
            >
              {displayName}
            </Link>
            <span className="break-all">{email}</span>
            {me.master?.active && (
              <span className="mt-1">
                <MasterBadge />
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-rule pt-2">
            <LanguageSwitch />
            <button
              type="button"
              onClick={() => void logout()}
              className="flex min-h-9 items-center gap-1.5 text-sm text-brand-text hover:underline"
            >
              <LogOut size={16} strokeWidth={1.75} aria-hidden />
              {t('auth.logout')}
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${t('nav.userMenu')}: ${displayName}`}
        title={displayName}
        className="flex min-h-10 w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm text-ink hover:bg-paper"
      >
        <span
          aria-hidden
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-brand-text"
        >
          {personInitials(displayName)}
        </span>
        <span className="min-w-0 flex-1 truncate font-medium">{personShortName(displayName)}</span>
        <ChevronUp size={14} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
      </button>
    </div>
  );
}

/** Lateral aberta: logo, busca, lista e rodapé com a pessoa e os avisos. */
function Sidebar({
  areas,
  me,
  idPrefix,
  initialArea,
  headerAction,
  onSearch,
  onNavigate,
}: {
  areas: NavArea[];
  me: Me;
  idPrefix: string;
  initialArea?: string;
  /** Recolher (computador) ou fechar (celular). */
  headerAction: ReactNode;
  onSearch: () => void;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-0 flex-col gap-3 bg-sheet px-3 py-4">
      <div className="flex items-start justify-between gap-2 px-1">
        <Link
          to="/"
          onClick={onNavigate}
          aria-label={t('nav.homeBrand')}
          className="flex flex-col gap-1"
        >
          <img src={logoUrl} alt="" className="h-7 w-auto self-start" />
          <span className="text-xs text-muted">{t('nav.brandCaption')}</span>
        </Link>
        {headerAction}
      </div>
      <button
        type="button"
        onClick={onSearch}
        className="flex min-h-10 shrink-0 items-center justify-between gap-2 rounded-md border border-rule bg-paper px-3 py-2 text-left text-[13px] text-muted hover:border-control-border"
      >
        <span className="flex items-center gap-2">
          <Search size={14} aria-hidden />
          {t('nav.search')}
        </span>
        <kbd className="hidden rounded border border-rule bg-sheet px-1.5 font-mono text-[11px] md:inline">
          Ctrl K
        </kbd>
      </button>
      <div className="loot-nav-scroll -mx-1 min-h-0 flex-1 px-1 py-1">
        <SidebarNav
          areas={areas}
          idPrefix={idPrefix}
          initialArea={initialArea}
          onNavigate={onNavigate}
        />
      </div>
      <div className="flex shrink-0 items-center gap-1 border-t border-rule pt-2">
        <UserMenu me={me} onNavigate={onNavigate} />
        <NotificationBell variant="rail" />
      </div>
    </div>
  );
}

/** Botão só com ícone da faixa recolhida: o nome vai na dica e para o leitor de tela. */
const RAIL_BUTTON =
  'flex size-9 shrink-0 items-center justify-center rounded-md text-rail-text hover:bg-paper hover:text-ink';

/**
 * Lateral recolhida (ADR 0071): uma faixa de ícones com Início, a busca, os departamentos, Ajuda,
 * Dados Mestres, Avisos e Sair. O clique num departamento abre a lateral nele.
 */
function CollapsedRail({
  areas,
  onSearch,
  onExpand,
}: {
  areas: NavArea[];
  onSearch: () => void;
  onExpand: (areaId?: string) => void;
}) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const current = locate(areas, pathname).area;
  const icon = (area: NavArea) => {
    const Icon = area.icon;
    const label = t(area.key);
    const active = area.id === current?.id;
    const className = `${RAIL_BUTTON} ${active ? 'bg-rail-active text-rail-active-text hover:bg-rail-active hover:text-rail-active-text' : ''}`;
    const body = <Icon size={20} strokeWidth={1.75} aria-hidden />;
    // Departamento em preparação (e o Início) não tem o que abrir: vai direto para a página.
    if (area.id === 'home' || screensOf(area).length === 0)
      return (
        <Link
          key={area.id}
          to={itemsOf(area)[0]!.to}
          aria-label={label}
          title={label}
          aria-current={active ? 'true' : undefined}
          className={className}
        >
          {body}
        </Link>
      );
    return (
      <button
        key={area.id}
        type="button"
        onClick={() => onExpand(area.id)}
        aria-label={label}
        title={label}
        aria-current={active ? 'true' : undefined}
        className={className}
      >
        {body}
      </button>
    );
  };
  return (
    <div className="flex h-full min-h-0 flex-col items-center gap-0.5 bg-rail py-2">
      <Link to="/" aria-label={t('nav.homeBrand')} className="flex shrink-0 py-1">
        <img src={symbolUrl} alt="" className="h-[26px] w-auto" />
      </Link>
      <button
        type="button"
        onClick={() => onExpand()}
        aria-label={t('nav.expand')}
        title={t('nav.expand')}
        className={`${RAIL_BUTTON} border border-control-border`}
      >
        <ChevronsRight size={16} aria-hidden />
      </button>
      <button
        type="button"
        onClick={onSearch}
        aria-label={t('nav.search')}
        title={`${t('nav.search')} (Ctrl K)`}
        className={RAIL_BUTTON}
      >
        <Search size={18} strokeWidth={1.75} aria-hidden />
      </button>
      <div className="loot-nav-scroll flex min-h-0 flex-1 flex-col items-center gap-0.5">
        {areas.filter((a) => !a.bottom).map(icon)}
        <div className="my-1 w-8 shrink-0 border-t border-rule" />
        {areas.filter((a) => a.bottom).map(icon)}
      </div>
      <div className="flex shrink-0 flex-col items-center gap-0.5 border-t border-rule pt-1">
        <NotificationBell variant="rail" />
        <button
          type="button"
          onClick={() => void logout()}
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
          className={RAIL_BUTTON}
        >
          <LogOut size={20} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );
}

/** Busca "Ir para tela…" (Ctrl K): procura em todas as áreas que a pessoa pode ver. */
function ScreenSearch({ areas, onClose }: { areas: NavArea[]; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const entries = useMemo(
    () =>
      areas.flatMap((area) =>
        itemsOf(area).map((item) => ({ item, label: t(item.key), area: t(area.key) })),
      ),
    [areas, t],
  );
  const normalize = (s: string) =>
    s
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();
  const found = entries.filter((e) =>
    normalize(`${e.label} ${e.area}`).includes(normalize(query.trim())),
  );

  function go(item: NavItem) {
    onClose();
    void navigate({ to: item.to });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/20 px-4 pt-[12vh] backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.search')}
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-rule bg-sheet shadow-[0_12px_32px_rgb(23_32_43/0.16)]"
      >
        <div className="flex items-center gap-2.5 border-b border-rule px-4">
          <Search size={18} strokeWidth={1.75} className="shrink-0 text-muted" aria-hidden />
          <input
            ref={inputRef}
            id="screen-search"
            value={query}
            placeholder={t('nav.searchPlaceholder')}
            aria-label={t('nav.searchPlaceholder')}
            onChange={(e) => {
              setQuery(e.target.value);
              setIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIndex((i) => Math.min(i + 1, found.length - 1));
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setIndex((i) => Math.max(i - 1, 0));
              }
              if (e.key === 'Enter' && found[index]) go(found[index].item);
            }}
            className="min-h-12 w-full bg-transparent text-sm text-ink outline-none placeholder:text-muted focus-visible:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-rule bg-paper px-1.5 font-mono text-[11px] text-muted md:inline">
            Esc
          </kbd>
        </div>
        <ul className="max-h-80 overflow-y-auto p-1.5">
          {found.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted">{t('nav.searchEmpty')}</li>
          )}
          {found.map((e, i) => {
            const Icon = e.item.icon;
            const active = i === index;
            return (
              <li key={e.item.to}>
                <button
                  type="button"
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => go(e.item)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm ${
                    active ? 'bg-paper text-ink' : 'text-ink'
                  }`}
                >
                  <Icon
                    size={18}
                    strokeWidth={1.75}
                    aria-hidden
                    className={`shrink-0 ${active ? 'text-brand-text' : 'text-muted'}`}
                  />
                  <span className="flex-1">{e.label}</span>
                  <span className="text-xs text-muted">{e.area}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * Menu do celular: a mesma lateral num painel por cima da tela. A tela de baixo continua montada
 * (formulários e filtros não se perdem). Fecha com X, Esc, toque fora ou ao mudar de tela; o foco
 * fica no painel e volta ao botão do menu.
 */
function MobileDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const panel = panelRef.current;
    panel?.querySelector<HTMLElement>('button, a[href]')?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), select, input, [tabindex]:not([tabindex="-1"])',
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, []);
  return (
    <div className="fixed inset-0 z-40 md:hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-ink/35"
        onMouseDown={() => closeRef.current()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.label')}
        className="absolute inset-y-0 left-0 flex w-80 max-w-[85vw] flex-col shadow-[0_12px_32px_rgb(23_32_43/0.16)]"
      >
        {children}
      </div>
    </div>
  );
}

/** Moldura das telas logadas: lateral única (ou a faixa recolhida) e o conteúdo. */
export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Guarda a página pedida antes de mandar para o login (o endereço muda durante o redirecionamento).
  const [requested] = useState(pathname);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Departamento escolhido na faixa recolhida, para a lateral abrir nele.
  const [expandTo, setExpandTo] = useState<string>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Mudou de tela pelo menu do celular: o painel fecha.
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    if (mobileOpen) setMobileOpen(false);
  }

  const areas = useMemo(() => visibleAreas((p) => can(me.data, p)), [me.data]);

  if (me.isPending)
    return (
      <div className="p-8">
        <Loading />
      </div>
    );
  if (me.isError)
    return (
      <div className="p-8">
        <ErrorBox error={me.error} />
      </div>
    );
  if (!me.data) {
    return <Navigate to="/login" search={{ returnTo: requested }} />;
  }

  const crumbs = breadcrumbs(areas, pathname);
  const setSaved = (value: boolean) => {
    saveCollapsed(value);
    setCollapsed(value);
  };
  const closeMobile = () => {
    setMobileOpen(false);
    menuButton.current?.focus();
  };

  // relative + overflow-hidden: nada (nem texto escondido para leitor de tela) faz a página inteira
  // rolar; quem rola é cada coluna. As peças antes do <main> só aparecem ou somem no lugar delas:
  // abrir, recolher ou fechar o menu nunca desmonta a tela aberta.
  return (
    <div className="relative flex min-h-dvh flex-col md:h-dvh md:flex-row md:overflow-hidden">
      {/* Celular: faixa no topo com o logo, os avisos e o botão do menu. */}
      <header className="flex items-center justify-between border-b border-rule bg-rail px-4 py-2 md:hidden">
        <Link to="/" aria-label={t('nav.homeBrand')}>
          <img src={logoUrl} alt="" className="h-6 w-auto" />
        </Link>
        <span className="flex items-center gap-2">
          <NotificationBell variant="header" />
          <button
            ref={menuButton}
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-expanded={mobileOpen}
            aria-haspopup="dialog"
            aria-label={t('nav.openMenu')}
            className="flex size-11 items-center justify-center rounded-md text-ink"
          >
            <Menu size={22} aria-hidden />
          </button>
        </span>
      </header>
      {mobileOpen && (
        <MobileDrawer onClose={closeMobile}>
          <Sidebar
            areas={areas}
            me={me.data}
            idPrefix="drawer"
            onNavigate={() => setMobileOpen(false)}
            onSearch={() => {
              setMobileOpen(false);
              setSearchOpen(true);
            }}
            headerAction={
              <button
                type="button"
                onClick={closeMobile}
                aria-label={t('nav.close')}
                className="flex size-11 items-center justify-center rounded-md text-ink"
              >
                <X size={22} aria-hidden />
              </button>
            }
          />
        </MobileDrawer>
      )}

      <ReportButton />
      <aside
        className={`relative hidden shrink-0 border-r border-rule md:flex md:flex-col ${
          collapsed ? 'w-16' : 'w-68'
        }`}
      >
        {collapsed ? (
          <CollapsedRail
            areas={areas}
            onSearch={() => setSearchOpen(true)}
            onExpand={(areaId) => {
              setExpandTo(areaId);
              setSaved(false);
            }}
          />
        ) : (
          <Sidebar
            areas={areas}
            me={me.data}
            idPrefix="side"
            initialArea={expandTo}
            onSearch={() => setSearchOpen(true)}
            headerAction={
              <button
                type="button"
                onClick={() => {
                  setExpandTo(undefined);
                  setSaved(true);
                }}
                aria-label={t('nav.collapse')}
                title={t('nav.collapse')}
                className="rounded-md border border-control-border p-1.5 text-muted hover:text-ink"
              >
                <ChevronsLeft size={14} aria-hidden />
              </button>
            }
          />
        )}
      </aside>

      {/* *:shrink-0: em janela baixa quem rola é a página; os blocos (inclusive a tabela) nunca
          encolhem até virar uma caixinha com rolagem própria. */}
      <main className="relative flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6 *:shrink-0 md:px-8">
        {crumbs.length > 0 && (
          <nav aria-label={t('nav.breadcrumb')} className="-mb-3 text-xs text-muted">
            <ol className="flex flex-wrap items-center gap-1.5">
              {crumbs.map((c, i) => (
                <li key={c.key} className="flex items-center gap-1.5">
                  {i > 0 && <span aria-hidden>›</span>}
                  {c.to ? (
                    <Link to={c.to} className="hover:text-ink hover:underline">
                      {t(c.key)}
                    </Link>
                  ) : (
                    <span aria-current={i === crumbs.length - 1 ? 'page' : undefined}>
                      {t(c.key)}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}
        <MfaNotice />
        <MasterDevNotice />
        <FocusNotice />
        <Outlet />
      </main>

      {searchOpen && <ScreenSearch areas={areas} onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

/**
 * Regra do master (ADR 0053 e 0075): o master está sem as travas de "a própria pessoa", sem
 * data-limite desde 27/10/2026, e cada uso fica registrado.
 */
function MasterDevNotice() {
  const { t, i18n } = useTranslation();
  const until = useMe().data?.master?.exceptionUntil;
  if (!until) return null;
  // Sem data-limite, uma linha basta: o aviso fica em toda tela e os detalhes abrem no clique.
  if (until === 'infinity') {
    return (
      <div role="status" className="rounded-lg bg-brand-soft px-4 py-2 text-sm text-ink">
        <details>
          <summary className="cursor-pointer">
            <span className="font-medium">{t('masterDevPhase.titlePermanent')}</span>
            <span className="ml-2 text-brand-text">{t('masterDevPhase.details')}</span>
          </summary>
          <p className="mt-1">{t('masterDevPhase.bodyPermanent')}</p>
        </details>
      </div>
    );
  }
  const date = new Date(`${until}T00:00:00Z`).toLocaleDateString(i18n.language, {
    timeZone: 'UTC',
  });
  return (
    <div role="status" className="rounded-lg border border-rule bg-brand-soft p-4 text-sm text-ink">
      <p className="font-medium">{t('masterDevPhase.title')}</p>
      <p>{t('masterDevPhase.body', { date })}</p>
    </div>
  );
}

/**
 * Entrou sem a segunda etapa e tem permissões sensíveis (S2, ADR 0020): elas ficam retidas até a
 * pessoa ativar o segundo fator no próprio perfil do login e entrar de novo.
 */
/** Empresa em foco (ADR 0100): à vista em toda tela, para ninguém ler a lista como se fosse tudo. */
function FocusNotice() {
  const { t } = useTranslation();
  const focus = useFocusEntity();
  if (!focus) return null;
  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rule bg-brand-soft px-4 py-2 text-sm"
    >
      <p className="font-medium">{t('focus.notice', { name: focus.name })}</p>
      <Button variant="secondary" onClick={() => setFocusEntity(null)}>
        {t('focus.seeAll')}
      </Button>
    </div>
  );
}

function MfaNotice() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const mfa = me.data?.mfa;
  if (!mfa || mfa.verified || mfa.withheld.length === 0) return null;
  const lang = i18n.language === 'en' ? 'en' : 'pt-BR';
  const labels = mfa.withheld.map(
    (code) => PERMISSIONS.find((p) => p.code === code)?.label[lang] ?? code,
  );
  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-lg border border-rule bg-brand-soft p-4 text-sm text-ink"
    >
      <p className="font-medium">{t('mfa.title')}</p>
      <p>{t('mfa.body')}</p>
      <ul className="ml-5 list-disc text-muted">
        {labels.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <p>{t('mfa.howTo')}</p>
      <div className="flex flex-wrap gap-2">
        <a
          href={mfa.setupUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-10 items-center rounded-md bg-accent px-4 py-2 font-medium text-on-accent hover:opacity-90"
        >
          {t('mfa.setup')}
        </a>
        <button
          type="button"
          onClick={() => void logout()}
          className="inline-flex min-h-10 items-center rounded-md border border-control-border bg-sheet px-4 py-2 font-medium text-ink hover:border-ink"
        >
          {t('mfa.relogin')}
        </button>
      </div>
    </div>
  );
}
